import LegalPage from './LegalPage';

const CONTENT = `# SiteScope — Terms of Use

**Last updated: 14 July 2026**

These Terms of Use ("Terms") govern your access to and use of the SiteScope platform ("Service"), operated by SiteScope PropTech ABN 22 740 236 438 ("we", "us", "our"). By accessing or using the Service, you agree to be bound by these Terms.

---

## 1. Acceptance of Terms

By creating an account, accessing, or using the Service in any way, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use and our Privacy Policy. If you do not agree to these Terms, you must not use the Service.

For Pro tier subscribers, by subscribing to the Pro tier you additionally acknowledge and agree to the Pro Tier Acceptable Use provisions set out in Section 4 of these Terms.

## 2. About the Service

SiteScope is a development application (DA) tracking and intelligence platform. The Service aggregates publicly available information from local council websites and the NSW Planning Portal to provide users with alerts, dashboards, mapping, and analysis tools for the construction industry.

The information displayed on SiteScope is sourced from government records that are publicly available under the Government Information (Public Access) Act 2009 (NSW) and licensed under Creative Commons Attribution 4.0 (CC BY 4.0) where applicable.

## 3. Accounts and Registration

To use the Service, you must create an account using a valid email address. You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account.

You must be at least 18 years of age to use the Service.

## 4. Subscription Tiers

### Standard Tier

The Standard tier provides limited DA tracking, dashboard analytics, map view, one watch zone, a weekly email digest of public DA activity, and links to the relevant council's own DA detail page. The weekly digest is enabled by default at signup and may be switched off in Settings or through the one-click unsubscribe link in every email. Inline SiteScope PDF viewing, instant alerts, tracked-status alerts, exhibition alerts, daily briefings, and supply-chain intelligence are Pro features. The Standard tier displays publicly available information only, including DA addresses, lodgement dates, status, development descriptions, and publicly recorded development value. No personal information is extracted or displayed at this tier.

### Pro Tier

The Pro tier provides additional features including project team intelligence, business/professional contact information where available, DA scoring, AI-generated briefs, brokered owner/applicant introduction requests, and expanded analytics. Access to the Pro tier requires business verification, including a valid Australian Business Number (ABN) in the construction industry and a current builder licence or architect registration number.

### Pro Tier — Acceptable Use

Pro tier users acknowledge and agree that contact information provided through the Service is for legitimate professional use only. Specifically, Pro tier users must not:

- Use contact information to cold call, harass, or make unsolicited contact with homeowners or property owners listed on development applications
- Attempt to bypass SiteScope's brokered introduction workflow for owner/applicant personal contact details
- Re-sell, redistribute, or on-sell any contact data obtained through the Service
- Use the Service to compile databases of personal information for marketing purposes unrelated to the specific development application
- Contact any individual in a manner that would constitute harassment or unwelcome contact under applicable law

Breach of these conditions may result in immediate termination of your Pro tier access without refund.

## 5. Fees and Payment

Subscription fees are charged monthly in advance via Stripe. All prices are in Australian Dollars (AUD) and include GST unless expressly stated otherwise.

You may cancel your subscription at any time. Cancellation takes effect at the end of your current billing period. No refunds are provided for partial billing periods, except as required by the Australian Consumer Law.

We reserve the right to change subscription pricing with 30 days' notice to existing subscribers.

## 6. Data Sources and Accuracy

The information displayed on SiteScope is sourced from publicly available government records, including local council DA tracking systems and the NSW Planning Portal Open Data API.

The Service provides information to assist your professional judgment but is not a substitute for independent verification. You are solely responsible for verifying information with the relevant council before making any business, investment, legal, or construction decision based on data shown in the Service.

We make no warranty that any particular information is accurate, current, or complete. Development application data is updated frequently but may not reflect the most current status of any particular application.

The Service is not a substitute for professional legal, planning, or construction advice.

## 7. Intellectual Property

All content, design, code, and functionality of the SiteScope platform is owned by SiteScope PropTech.

Development application data displayed on the Service is sourced from government records and remains the property of the relevant government authority. Where data is provided under Creative Commons Attribution 4.0 (CC BY 4.0), we comply with the attribution requirements of that licence.

You may not automate access to, scrape, crawl, bulk export, or systematically extract data from the SiteScope platform. You may not bypass technical limits, rate limits, access controls, subscription entitlements, or monitoring systems. You may not share account access for the purpose of extracting data, and you may not use AI agents, bots, browser automation, scripts, or similar tools to collect SiteScope data at scale.

You may not reuse derived or value-added data generated by the Service (including our categorisations, extracted supply chain information, AI-generated summaries, contact matching, recommendation signals, and analytics) to create a competing product or service. We may suspend, revoke, watermark, log, or restrict access where we reasonably suspect scraping, resale, unauthorised redistribution, or misuse. This restriction does not apply to the underlying public government data, which remains available to you directly from its original sources.

## 8. Privacy

Your use of the Service is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal information. By using the Service, you consent to the practices described in the Privacy Policy.

## 9. Limitation of Liability

To the maximum extent permitted by Australian law, SiteScope PropTech is not liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service.

Our total liability for any claim arising from the Service is limited to the fees you have paid in the 12 months preceding the claim.

We are not responsible for any loss or damage arising from decisions made based on information provided through the Service.

Nothing in these Terms excludes or limits any rights you may have under the Australian Consumer Law that cannot be excluded or limited by contract.

## 10. Modifications

We may modify these Terms at any time by posting updated Terms on the Service. Material changes will be notified to you via email at least 14 days before they take effect. Your continued use of the Service after the effective date constitutes acceptance of the modified Terms.

## 11. Termination

We may suspend or terminate your account if you breach these Terms. Upon termination, your right to access the Service ceases immediately. Any data associated with your account may be deleted after a reasonable retention period.

You may close your account at any time by contacting us.

## 12. Governing Law

These Terms are governed by the laws of New South Wales, Australia. Any disputes arising from these Terms will be resolved in the courts of New South Wales.

If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.

## 13. Contact

For questions about these Terms, contact us at:

**SiteScope PropTech**
ABN: 22 740 236 438
Email: support@sitescopeapp.com
Website: sitescopeapp.com`;

export default function TermsPage() {
  return <LegalPage title="Terms of Use" content={CONTENT} />;
}
